import {
    type TextStyle,
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    SidebarActionPlacementConstantType,
    SidebarActiveWeightConstantType,
    SidebarRowMeasuresConstantType,
    SidebarRowRadiusConstantType,
    SidebarRowColorsConstantType,
    SidebarRootDynamicStyleType,
    SidebarMeasuresConstantType,
    SidebarFrameConstantType,
    SidebarWidthConstantType,
    SidebarActionPlacement,
    SidebarRowMeasures,
    SidebarRowColors,
    SidebarMeasures,
    SidebarSize
} from "./type";
import {
    getTypeColors
} from "../../helpers/theme/typeColors";
import type {
    Mutable
} from "../../types";
import {
    getSurfaceStyle
} from "../../helpers/theme/elevation";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    getHitArea
} from "../../helpers/theme/control";

export const SIDEBAR_LABEL_VARIANTS: Record<SidebarSize, keyof NCoreUIKit.Typography> = {
    medium: "labelMediumSize",
    large: "labelLargeSize",
    small: "labelSmallSize"
};

export const SIDEBAR_ACTIVE_WEIGHT_VARIANT: keyof NCoreUIKit.Typography = "titleSmallSize";

export const SIDEBAR_SUB_LABEL_VARIANT: keyof NCoreUIKit.Typography = "bodySmallSize";

export const getSidebarMeasures = ({
    controlPaddings,
    controlHeights,
    shapeConfigs,
    spaces
}: SidebarMeasuresConstantType): SidebarMeasures => {
    return {
        headerHeight: controlHeights.large + spaces.spacingSm * 2,
        railWidth: controlHeights.large + spaces.spacingLg,
        expandedWidth: shapeConfigs.sideListWidth,
        padding: spaces.spacingLg / 2,
        square: controlHeights.large,
        gap: controlPaddings.small,
        frame: spaces.spacingSm
    };
};

export const getSidebarRowMeasures = ({
    controlHeights,
    touchTargets,
    pointerType,
    iconSizes,
    square,
    size
}: SidebarRowMeasuresConstantType): SidebarRowMeasures => {
    const iconSize = iconSizes[size];

    return {
        height: pointerType === "coarse" ? Math.max(controlHeights[size], touchTargets.preferred) : controlHeights[size],
        labelVariant: SIDEBAR_LABEL_VARIANTS[size],
        inset: (square - iconSize) / 2,
        iconSize
    };
};

export const getSidebarRowColors = ({
    isDisabled,
    isActive,
    colors
}: SidebarRowColorsConstantType): SidebarRowColors => {
    const currentColors = getTypeColors({
        isSelected: isActive,
        variant: "ghost",
        type: "neutral",
        isDisabled,
        colors
    });

    if(isDisabled) {
        return {
            subTitleColor: currentColors.titleColor,
            titleColor: currentColors.titleColor,
            iconColor: currentColors.iconColor,
            containerColor: "transparent",
            iconColorKey: "disabled"
        };
    }

    if(isActive) {
        return {
            containerColor: currentColors.containerColor,
            subTitleColor: colors.content.text.mid,
            titleColor: currentColors.titleColor,
            iconColor: currentColors.iconColor,
            iconColorKey: "high"
        };
    }

    return {
        subTitleColor: colors.content.text.mid,
        titleColor: currentColors.titleColor,
        iconColor: colors.content.icon.mid,
        containerColor: "transparent",
        iconColorKey: "mid"
    };
};

export const getSidebarActiveWeight = ({
    typography
}: SidebarActiveWeightConstantType): TextStyle["fontWeight"] => {
    return typography[SIDEBAR_ACTIVE_WEIGHT_VARIANT].fontWeight as TextStyle["fontWeight"];
};

export const getSidebarActionPlacement = ({
    touchTargets,
    pointerType,
    iconSizes,
    height,
    spaces,
    inset
}: SidebarActionPlacementConstantType): SidebarActionPlacement => {
    const visualSize = Math.min(touchTargets.fine, height - spaces.spacingXs * 2);

    const {
        minHeight: hitSize
    } = getHitArea({
        touchTargets,
        pointerType,
        visualSize
    });

    return {
        endOffset: inset - (visualSize - iconSizes.small) / 2 - (hitSize - visualSize) / 2,
        visualSize,
        hitSize
    };
};

export const getSidebarRowRadius = ({
    measures,
    radiuses,
    variant,
    borders
}: SidebarRowRadiusConstantType): number => {
    if(variant !== "floating") {
        return radiuses.actions;
    }

    return concentric({
        inset: measures.padding + borders.line,
        outer: radiuses.surface,
        role: radiuses.actions
    });
};

export const getSidebarFrameInset = ({
    measures,
    variant
}: Omit<SidebarFrameConstantType, "collapseBehaviour">): number => {
    return variant === "floating" ? measures.frame : 0;
};

export const getSidebarGutter = ({
    collapseBehaviour,
    measures,
    variant
}: SidebarFrameConstantType): number => {
    return variant === "inset" && collapseBehaviour === "offcanvas" ? measures.frame : 0;
};

export const getSidebarWidth = ({
    collapseBehaviour,
    isCollapsed,
    measures,
    variant
}: SidebarWidthConstantType): number => {
    if(collapseBehaviour === "offcanvas" && isCollapsed) {
        return getSidebarGutter({
            collapseBehaviour,
            measures,
            variant
        });
    }

    const frameInset = getSidebarFrameInset({
        measures,
        variant
    });

    return (isCollapsed ? measures.railWidth : measures.expandedWidth) + frameInset * 2;
};

const stylesheet = StyleSheet.create({
    container: {
        flexDirection: "column",
        alignSelf: "stretch",
        position: "relative",
        flexShrink: 0,
        minHeight: 0,
        flexGrow: 0,
        minWidth: 0
    },
    offcanvas: {
        position: "absolute"
    },
    clip: {
        overflow: "hidden",
        minHeight: 0,
        minWidth: 0,
        flex: 1
    },
    hidden: {
        display: "none"
    },
    phone: {
        minHeight: 0,
        flex: 1
    }
});

export const useStyles = ({
    collapseBehaviour,
    shapeConfigs,
    shadowAlpha,
    elevations,
    isHidden,
    measures,
    radiuses,
    borders,
    variant,
    colors,
    side
}: SidebarRootDynamicStyleType) => {
    const frameInset = getSidebarFrameInset({
        measures,
        variant
    });

    const styles = {
        panel: {} as Mutable<ViewStyle>
    };

    if(variant === "sidebar") {
        Object.assign(styles.panel, {
            backgroundColor: colors.content.container.mid
        }, side === "left" ? {
            borderEndColor: colors.content.border.subtle,
            borderEndWidth: borders.separator
        } : {
            borderStartColor: colors.content.border.subtle,
            borderStartWidth: borders.separator
        });
    }

    if(variant === "floating") {
        Object.assign(styles.panel, getSurfaceStyle({
            surface: "card",
            shadowAlpha,
            elevations,
            borders,
            colors
        }), getCornerStyle({
            radius: radiuses.surface,
            shapeConfigs
        }));
    }

    if(collapseBehaviour === "offcanvas") {
        Object.assign(styles.panel, stylesheet.offcanvas, {
            width: measures.expandedWidth,
            bottom: frameInset,
            top: frameInset
        });
    } else {
        styles.panel.margin = frameInset;
    }

    if(isHidden) {
        Object.assign(styles.panel, stylesheet.hidden);
    }

    return styles;
};
export default stylesheet;
